import { create } from "zustand";

interface UIState {
  sidebarCollapsed: boolean;
  sidebarPinned: boolean;
  activeModule: string;
  selectedProjectId: string | null;
  tableSelection: Set<string>;
  editingCell: { rowId: string; columnId: string } | null;
  adminMode: boolean;
  theme: "dark" | "light";

  toggleSidebar: () => void;
  toggleSidebarPin: () => void;
  setActiveModule: (module: string) => void;
  setSelectedProjectId: (id: string | null) => void;
  setTableSelection: (selection: Set<string>) => void;
  toggleRowSelection: (id: string) => void;
  clearSelection: () => void;
  setEditingCell: (cell: { rowId: string; columnId: string } | null) => void;
  setAdminMode: (mode: boolean) => void;
  toggleTheme: () => void;
  setTheme: (theme: "dark" | "light") => void;
}

function applyTheme(theme: "dark" | "light") {
  if (theme === "light") {
    document.documentElement.setAttribute("data-theme", "light");
  } else {
    document.documentElement.removeAttribute("data-theme");
  }
}

const savedTheme = (localStorage.getItem("tga_theme") as "dark" | "light") || "dark";
applyTheme(savedTheme);

export const useUIStore = create<UIState>((set) => ({
  sidebarCollapsed: false,
  sidebarPinned: false,
  activeModule: "projects",
  selectedProjectId: null,
  tableSelection: new Set(),
  editingCell: null,
  adminMode: true,
  theme: savedTheme,

  toggleSidebar: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
  toggleSidebarPin: () => set((s) => ({ sidebarPinned: !s.sidebarPinned })),
  setActiveModule: (module) => set({ activeModule: module }),
  setSelectedProjectId: (id) => set({ selectedProjectId: id }),
  setTableSelection: (selection) => set({ tableSelection: selection }),
  toggleRowSelection: (id) =>
    set((s) => {
      const next = new Set(s.tableSelection);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return { tableSelection: next };
    }),
  clearSelection: () => set({ tableSelection: new Set() }),
  setEditingCell: (cell) => set({ editingCell: cell }),
  setAdminMode: (mode) => {
    localStorage.setItem("tga_admin_mode", String(mode));
    set({ adminMode: mode });
  },
  toggleTheme: () =>
    set((s) => {
      const next = s.theme === "dark" ? "light" : "dark";
      localStorage.setItem("tga_theme", next);
      applyTheme(next);
      return { theme: next };
    }),
  setTheme: (theme) => {
    localStorage.setItem("tga_theme", theme);
    applyTheme(theme);
    set({ theme });
  },
}));
